import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Check, Copy, Loader2, Lock, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { useInterviewScheduling } from '@/hooks/useInterviewScheduling';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { getInterviewSchedulingBlock } from '@/lib/interviewSchedulingAccess';
import type { Interview } from '@/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateName: string;
  candidateEmail: string;
  positionTitle: string;
  companyId: string;
  positionId: string;
  applicationId: string;
  assignedMemberIds?: string[];
  onScheduled?: () => void;
  mode?: 'create' | 'edit';
  editInterview?: Interview | null;
}

const InterviewSchedulingModal = ({
  open, onOpenChange, candidateName, candidateEmail, positionTitle, companyId, positionId, applicationId,
  assignedMemberIds, onScheduled, mode = 'create', editInterview = null,
}: Props) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { data: billingStatus } = useBillingStatus();
  const isEditMode = mode === 'edit' && !!editInterview;
  const schedulingBlock = !isEditMode ? getInterviewSchedulingBlock(billingStatus) : null;

  const hook = useInterviewScheduling(
    open, onOpenChange, companyId, positionId, applicationId, assignedMemberIds, onScheduled,
    mode, editInterview,
  );

  const blockedTitle = schedulingBlock === 'trial_not_included'
    ? t('interviewModal.blocked.trialTitle')
    : t('interviewModal.blocked.creditsTitle');
  const blockedBody = schedulingBlock === 'trial_not_included'
    ? t('interviewModal.blocked.trialBody')
    : t('interviewModal.blocked.creditsBody');

  return (
    <Dialog open={open} onOpenChange={hook.handleClose}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader className="pb-4">
          <DialogTitle>{hook.isEditMode ? t('interviewModal.titleEdit') : t('interviewModal.titleTranscript')}</DialogTitle>
          <p className="text-sm text-muted-foreground">{candidateName} · {positionTitle}</p>
        </DialogHeader>

        {schedulingBlock ? (
          <div className="flex flex-col items-center py-8 px-2 text-center space-y-4">
            <div className="h-14 w-14 rounded-full bg-amber-500/10 flex items-center justify-center">
              <Lock className="h-7 w-7 text-amber-700 dark:text-amber-300" />
            </div>
            <div className="space-y-2 max-w-md">
              <h3 className="text-lg font-semibold">{blockedTitle}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{blockedBody}</p>
            </div>
            <div className="flex w-full max-w-sm flex-col gap-2 pt-2">
              <Button
                className="w-full"
                onClick={() => {
                  hook.handleClose();
                  navigate('/profile?tab=billing');
                }}
              >
                {t('interviewModal.blocked.upgradeCta')}
              </Button>
              <Button variant="outline" className="w-full" onClick={hook.handleClose}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : hook.success ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="h-16 w-16 rounded-full bg-success/10 flex items-center justify-center">
              <Check className="h-8 w-8 text-success" />
            </div>
            <h3 className="text-lg font-semibold">{hook.isEditMode ? t('interviewModal.updated') : t('interviewModal.scheduled')}</h3>
            <p className="text-sm text-muted-foreground text-center">
              {hook.date && (
                <>
                  {format(hook.date, 'EEEE, MMMM d, yyyy')}
                  {hook.derivedDurationMinutes != null && hook.derivedDurationMinutes > 0
                    ? ` · ${hook.time}–${hook.endTime} · ${hook.derivedDurationMinutes} min`
                    : ` · ${hook.time}`}
                </>
              )}
            </p>

            <div className="w-full space-y-3 mt-4">
              {hook.meetLink && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
                  <Video className="h-4 w-4 text-primary shrink-0" />
                  <a href={hook.meetLink} className="text-sm text-primary hover:underline flex-1 truncate">
                    {hook.meetLink.replace('https://', '')}
                  </a>
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { navigator.clipboard.writeText(hook.meetLink); toast.success(t('appDetail.linkCopied')); }}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}

              <div className="pt-2">
                <p className="text-xs text-muted-foreground mb-2">{t('appDetail.participants')}</p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs px-2 py-1 rounded-md bg-primary/10 text-primary">{candidateName} ({t('interviewModal.candidate')})</span>
                  {hook.participants.map(p => (
                    <span key={p.userId} className="text-xs px-2 py-1 rounded-md bg-muted text-muted-foreground">{p.name}</span>
                  ))}
                </div>
              </div>
            </div>

            <Button className="mt-4 w-full" onClick={hook.handleClose}>{t('common.close')}</Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 min-w-0">
                <Label>{t('interviewModal.dateLabel')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn('w-full justify-start text-left font-normal', !hook.date && 'text-muted-foreground')}>
                      <CalendarIcon className="h-4 w-4 mr-2 shrink-0" />
                      <span className="truncate">{hook.date ? format(hook.date, 'PPP') : t('interviewModal.pickDate')}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={hook.date}
                      onSelect={hook.setDate}
                      disabled={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const day = new Date(d);
                        day.setHours(0, 0, 0, 0);
                        return day < today;
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2 min-w-0">
                <Label>{t('interviewModal.timeLabel')}</Label>
                <Input
                  type="time"
                  step={60}
                  value={hook.time}
                  onChange={e => hook.setTime(e.target.value)}
                  className="font-mono text-sm w-full"
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label>{t('interviewModal.endTimeLabel')}</Label>
                <Input
                  type="time"
                  step={60}
                  value={hook.endTime}
                  onChange={e => hook.setEndTime(e.target.value)}
                  className="font-mono text-sm w-full"
                />
              </div>
            </div>

            {hook.derivedDurationMinutes != null && hook.derivedDurationMinutes > 0 && (
              <p className="text-xs text-muted-foreground">
                {t('interviewModal.derivedDurationHint', { minutes: hook.derivedDurationMinutes })}
              </p>
            )}

            <div className="space-y-2">
              <Label>{t('interviewModal.externalMeetingUrl')}</Label>
              <p className="text-xs text-muted-foreground">{t('interviewModal.externalMeetingUrlDesc')}</p>
              <Input
                placeholder={t('interviewModal.externalMeetingUrlPlaceholder')}
                value={hook.externalMeetingUrl}
                onChange={e => hook.setExternalMeetingUrl(e.target.value)}
                className="font-mono text-sm w-full"
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('interviewModal.notes')}</Label>
              <Textarea
                placeholder={t('interviewModal.notesPlaceholder')}
                value={hook.notes}
                onChange={e => hook.setNotes(e.target.value)}
                rows={3}
                className="w-full resize-y"
              />
            </div>

            <div className="space-y-2 pt-2">
              <Button className="w-full" onClick={hook.handleSubmit} disabled={!hook.canSubmit || hook.submitting}>
                {hook.submitting
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {hook.isEditMode ? t('interviewModal.updating') : t('interviewModal.scheduling')}</>
                  : (hook.isEditMode ? t('interviewModal.update') : t('interviewModal.schedule'))}
              </Button>
              <Button variant="outline" className="w-full" onClick={hook.handleClose}>{t('common.cancel')}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InterviewSchedulingModal;
